import * as z from "zod";
import {
  anyOfFilters,
  countByFiltersInput,
  cursorPagination,
  flag,
  pagination,
  uuidV7,
  validationIssue,
} from "../index";
import { attributesStatuses, linesheetStatuses, priceStatuses, syncStatuses } from "./enums";
import { code, codeName, version } from "./fields";

// Header of a linesheet (50-api/catalog.md §2.1).

const isoDate = z.iso.date();

/** Linesheet by id (uuid) or by name. */
export const getLinesheetInput = z.object({
  key: z.string().min(1).max(200),
});
export type GetLinesheetInput = z.input<typeof getLinesheetInput>;

/** Orders of the linesheet list; the id breaks ties. */
const linesheetSorts = ["-updatedAt", "name", "season"] as const;

const linesheetFilters = {
  brand: z.uuid().optional(),
  season: z.string().max(20).optional(),
  status: z.enum(linesheetStatuses).optional(),
  priceStatus: z.enum(priceStatuses).optional(),
  bcSyncStatus: z.enum(syncStatuses).optional(),
  joorSyncStatus: z.enum(syncStatuses).optional(),
  attributesStatus: z.enum(attributesStatuses).optional(),
  /** Only the ones whose prices wait for a recalculation after a change of the Price Setup. */
  pricesStale: flag.optional(),
  /** Only the archived ones instead of the others; external Actors never see archived linesheets. */
  archived: flag.optional(),
  /** Part of the name, without case and accents (ADR-117). */
  q: z.string().trim().min(1).max(100).optional(),
};

/** The linesheets in at least one of these sets of the filters (ADR-302): the queues of the linesheets, to do. */
const anyOfLinesheets = { anyOf: anyOfFilters(linesheetFilters).optional() };

/** Linesheets visible to the Actor, most recently updated first. */
export const listLinesheetsInput = z.object({
  ...linesheetFilters,
  ...anyOfLinesheets,
  sort: z.enum(linesheetSorts).default("-updatedAt"),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListLinesheetsInput = z.input<typeof listLinesheetsInput>;

/** How many linesheets the list has with the same filters, and when the one untouched for longest was updated. */
export const countLinesheetsInput = z.object({ ...linesheetFilters, ...anyOfLinesheets });
export type CountLinesheetsInput = z.input<typeof countLinesheetsInput>;
/** The same count under several filters, in one call and one transaction (ADR-269): the queues of the home. */
export const countLinesheetsByFiltersInput = countByFiltersInput(countLinesheetsInput);
export type CountLinesheetsByFiltersInput = z.input<typeof countLinesheetsByFiltersInput>;

/**
 * An action on a linesheet (submit, approve, archive, delete…). `version` is the one the client read: a linesheet
 * changed in the meantime is left as it is (409).
 */
export const linesheetActionInput = z.object({
  linesheetId: z.uuid(),
  version,
});
export type LinesheetActionInput = z.input<typeof linesheetActionInput>;

/** The gateway routes split the input: the id in the path, the rest in the body (or in the query for DELETE). */
export const linesheetParams = linesheetActionInput.pick({ linesheetId: true });
export const versionBody = linesheetActionInput.pick({ version: true });

/** The id comes from the client (ADR-046): a retry by the same user returns the linesheet already created. */
export const createLinesheetInput = z.object({
  id: uuidV7,
  /** A brand user can only name one of its own brands. */
  brandOrgId: z.uuid(),
  season: code,
  name: z.string().trim().min(1).max(200),
  category: code.optional(),
  salesCampaignId: z.uuid().optional(),
  inboundDeliveryFrom: isoDate.optional(),
  inboundDeliveryTo: isoDate.optional(),
  outboundDeliveryFrom: isoDate.optional(),
  outboundDeliveryTo: isoDate.optional(),
});
export type CreateLinesheetInput = z.input<typeof createLinesheetInput>;

/** PATCH: an absent field stays as it is, `null` empties it. */
export const updateLinesheetInput = z.object({
  linesheetId: z.uuid(),
  version,
  name: z.string().trim().min(1).max(200).optional(),
  season: code.optional(),
  category: code.nullable().optional(),
  salesCampaignId: z.uuid().nullable().optional(),
  inboundDeliveryFrom: isoDate.nullable().optional(),
  inboundDeliveryTo: isoDate.nullable().optional(),
  outboundDeliveryFrom: isoDate.nullable().optional(),
  outboundDeliveryTo: isoDate.nullable().optional(),
  /** `read` for the external roles: sending it answers 403 field_read_only. */
  syncToBc: z.boolean().optional(),
  /** FR-M2-07. */
  syncToJoor: z.boolean().optional(),
});
export type UpdateLinesheetInput = z.input<typeof updateLinesheetInput>;
export const updateLinesheetBody = updateLinesheetInput.omit({ linesheetId: true });

/** The note goes to the audit, where the notification to the brand reads it. */
export const returnLinesheetInput = linesheetActionInput.extend({ note: z.string().trim().min(1).max(2000) });
export type ReturnLinesheetInput = z.input<typeof returnLinesheetInput>;
export const returnLinesheetBody = returnLinesheetInput.omit({ linesheetId: true });

export const reopenLinesheetInput = returnLinesheetInput.extend({ to: z.enum(["pending", "draft"]) });
export type ReopenLinesheetInput = z.input<typeof reopenLinesheetInput>;
export const reopenLinesheetBody = reopenLinesheetInput.omit({ linesheetId: true });

/** The checks an action runs, without running it: the screen shows what is missing before the user tries. */
const linesheetCheckPurposes = ["submit", "approve", "sync", "approve_prices"] as const;
export type LinesheetCheckPurpose = (typeof linesheetCheckPurposes)[number];
export const checkLinesheetInput = z.object({ linesheetId: z.uuid(), for: z.enum(linesheetCheckPurposes) });
export type CheckLinesheetInput = z.input<typeof checkLinesheetInput>;
export const checkLinesheetQuery = checkLinesheetInput.omit({ linesheetId: true });

/** The values of the linesheet form (ADR-048): nothing to choose. */
export const getLinesheetOptionsInput = z.object({});
export type GetLinesheetOptionsInput = z.input<typeof getLinesheetOptionsInput>;

/** Newest first. */
export const getLinesheetHistoryInput = z.object({
  linesheetId: z.uuid(),
  ...cursorPagination({ defaultLimit: 50, maxLimit: 100 }),
});
export type GetLinesheetHistoryInput = z.input<typeof getLinesheetHistoryInput>;
export const linesheetHistoryQuery = getLinesheetHistoryInput.omit({ linesheetId: true });

/** Header of a linesheet: in the list, in the detail and after an action. */
export const linesheetSummaryDto = z
  .object({
    id: z.uuid(),
    name: z.string(),
    brandOrgId: z.uuid().optional(),
    brand: z.string().optional(),
    season: z.string().optional(),
    category: z.string().nullable().optional(),
    salesCampaignId: z.uuid().nullable().optional(),
    salesCampaign: z.string().nullable().optional(),
    status: z.enum(linesheetStatuses).optional(),
    priceStatus: z.enum(priceStatuses).optional(),
    attributesStatus: z.enum(attributesStatuses).optional(),
    bcSyncStatus: z.enum(syncStatuses).optional(),
    /** The sync to Joor, which follows the one to Business Central when `syncToJoor` (spec v0.2 §4 rule 9). */
    joorSyncStatus: z.enum(syncStatuses).optional(),
    syncToBc: z.boolean().optional(),
    syncToJoor: z.boolean().optional(),
    inboundDeliveryFrom: z.iso.date().nullable().optional(),
    inboundDeliveryTo: z.iso.date().nullable().optional(),
    outboundDeliveryFrom: z.iso.date().nullable().optional(),
    outboundDeliveryTo: z.iso.date().nullable().optional(),
    lineCount: z.number().int(),
    /** First submission by the brand: from then on it asks for line deletions instead of deleting. */
    firstSubmittedAt: z.iso.datetime().nullable().optional(),
    /** The Price Setup changed after the prices were calculated: they wait for a recalculation (ADR-060). */
    pricesStaleSince: z.iso.datetime().nullable().optional(),
    archivedAt: z.iso.datetime().nullable().optional(),
    version: z.number().int(),
    updatedAt: z.iso.datetime().optional(),
  })
  .meta({ id: "LinesheetSummary" });

/** What remains of a deleted linesheet. */
export const deletedLinesheetDto = z.object({ id: z.uuid() });

/** What an action still needs, per field (and row). */
export const linesheetCheckDto = z
  .object({ ready: z.boolean(), issues: z.array(validationIssue) })
  .meta({ id: "LinesheetCheck" });

/**
 * The values the linesheet form offers (ADR-048): only brands the Actor may work for, seasons and campaigns not
 * archived.
 */
export const linesheetOptionsDto = z
  .object({
    brands: z.array(z.object({ orgId: z.uuid(), name: z.string() })),
    seasons: z.array(codeName),
    categories: z.array(codeName),
    salesCampaigns: z.array(z.object({ id: z.uuid(), name: z.string(), season: z.string().nullable() })),
  })
  .meta({ id: "LinesheetOptions" });
